import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import * as api from '../api/purchase-invoices';

export const usePurchaseInvoicesPaginated = (params) =>
  useInfiniteQuery({
    queryKey: ['purchase-invoices', 'paginated', params],
    queryFn: ({ pageParam = 1 }) =>
      api.getPurchaseInvoicesPaginated({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    placeholderData: (prev) => prev,
  });

export const usePurchaseInvoice = (uuid) =>
  useQuery({
    queryKey: ['purchase-invoices', uuid],
    queryFn: () => api.getPurchaseInvoice(uuid),
    enabled: !!uuid,
  });

export const useCreatePurchaseInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createPurchaseInvoice(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-invoices'] }),
  });
};

export const useReceiveGoods = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.receiveGoods(uuid, data),
    onSuccess: (_, { uuid }) => {
      qc.invalidateQueries({ queryKey: ['purchase-invoices'] });
      qc.invalidateQueries({ queryKey: ['purchase-invoices', uuid] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['inventory-lots'] });
    },
  });
};

export const useUpdatePurchaseInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.updatePurchaseInvoice(uuid, data),
    onSuccess: (_, { uuid }) => {
      qc.invalidateQueries({ queryKey: ['purchase-invoices'] });
      qc.invalidateQueries({ queryKey: ['purchase-invoices', uuid] });
    },
  });
};

export const useUpdatePaymentStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, paymentStatus, businessUuid }) =>
      api.updatePaymentStatus(uuid, paymentStatus, businessUuid),
    onSuccess: (_, { uuid }) => {
      qc.invalidateQueries({ queryKey: ['purchase-invoices'] });
      qc.invalidateQueries({ queryKey: ['purchase-invoices', uuid] });
    },
  });
};

export const useDeletePurchaseInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.deletePurchaseInvoice(uuid, businessUuid),
    onSuccess: (_, { uuid }) => {
      qc.removeQueries({ queryKey: ['purchase-invoices', uuid] });
      qc.invalidateQueries({ queryKey: ['purchase-invoices'], exact: false });
    },
  });
};

export const useImportInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData) => api.importInvoice(formData),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-invoices'] }),
  });
};
